// ============================================================
// Chess Arena — Terrain System
// ============================================================
// Extensible terrain type registry with procedural placement.
// Loads terrain configs from GameSettings, places terrain on
// the board, and exposes query helpers for passability / stealth / LOS.
// ============================================================

import {
  GridPos,
  TerrainType,
  TerrainTile,
  TerrainTypeConfig,
  BOARD_COLS,
  BOARD_ROWS,
} from '../core/types';
import eventBus from '../core/eventBus';

/**
 * Extended GameSettings shape — only the slice we need.
 * The full GameSettingsConfig lives in types.ts; this avoids a
 * circular-dependency if GameSettings grows.
 */
interface TerrainSettingsSlice {
  terrainTypes: Record<string, TerrainTypeConfig>;
}

export class TerrainSystem {
  private scene: Phaser.Scene;
  private terrainMap: Map<string, TerrainTile> = new Map();
  private terrainSprites: Map<string, Phaser.GameObjects.Sprite> = new Map();
  private registry: Record<string, TerrainTypeConfig> = {};

  // Board rendering helpers (injected so system is layout-agnostic)
  private boardOriginX = 0;
  private boardOriginY = 0;
  private cellSize = 80;
  private cellH = 80;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  // ------------------------------------------------------------------
  // Initialisation
  // ------------------------------------------------------------------

  /**
   * Load terrain type configs from the settings object (parsed GameSettings.json).
   */
  loadTerrainConfigs(settings: TerrainSettingsSlice): void {
    this.registry = { ...settings.terrainTypes };
  }

  /**
   * Set board rendering parameters so sprites land on the correct pixels.
   */
  setBoardLayout(originX: number, originY: number, cellSize: number, cellH?: number): void {
    this.boardOriginX = originX;
    this.boardOriginY = originY;
    this.cellSize = cellSize;
    this.cellH = cellH ?? cellSize;
  }

  // ------------------------------------------------------------------
  // Procedural Placement
  // ------------------------------------------------------------------

  /**
   * Randomly place terrain on valid cells.
   * Valid cells exclude spawn rows and the center row.
   */
  placeTerrainProcedurally(maxRocks: number, maxBushes: number): void {
    this.clearTerrain();

    const validRows = this.getValidRows();
    const available = this.buildAvailableCells(validRows);

    const rockCount = maxRocks > 0 ? Phaser.Math.Between(1, maxRocks) : 0;
    const bushCount = maxBushes > 0 ? Phaser.Math.Between(1, maxBushes) : 0;

    this.placeRandomTerrain('rock', rockCount, available);
    this.placeRandomTerrain('bush', bushCount, available);
  }

  private getValidRows(): number[] {
    const centerRow = Math.floor(BOARD_ROWS / 2);
    const excluded = new Set([0, 1, centerRow, BOARD_ROWS - 2, BOARD_ROWS - 1]);
    const valid: number[] = [];
    for (let r = 0; r < BOARD_ROWS; r++) {
      if (!excluded.has(r)) valid.push(r);
    }
    return valid;
  }

  private buildAvailableCells(validRows: number[]): GridPos[] {
    const cells: GridPos[] = [];
    for (const row of validRows) {
      for (let col = 0; col < BOARD_COLS; col++) {
        cells.push({ col, row });
      }
    }
    return cells;
  }

  private placeRandomTerrain(type: TerrainType, count: number, available: GridPos[]): void {
    for (let i = 0; i < count && available.length > 0; i++) {
      const idx = Phaser.Math.Between(0, available.length - 1);
      const pos = available.splice(idx, 1)[0];
      this.addTerrain(type, pos);
    }
  }

  // ------------------------------------------------------------------
  // Terrain CRUD
  // ------------------------------------------------------------------

  addTerrain(type: TerrainType, pos: GridPos): void {
    const key = this.posKey(pos);
    const tile: TerrainTile = { type, pos: { ...pos } };
    this.terrainMap.set(key, tile);
    this.renderTerrainSprite(tile);
  }

  removeTerrain(pos: GridPos): void {
    const key = this.posKey(pos);
    this.terrainMap.delete(key);
    const sprite = this.terrainSprites.get(key);
    if (sprite) {
      sprite.destroy();
      this.terrainSprites.delete(key);
    }
  }

  clearTerrain(): void {
    for (const sprite of this.terrainSprites.values()) {
      sprite.destroy();
    }
    this.terrainSprites.clear();
    this.terrainMap.clear();
  }

  // ------------------------------------------------------------------
  // Queries
  // ------------------------------------------------------------------

  getTerrainAt(pos: GridPos): TerrainTile | undefined {
    return this.terrainMap.get(this.posKey(pos));
  }

  getTerrainConfig(type: TerrainType | string): TerrainTypeConfig | undefined {
    return this.registry[type];
  }

  isPassable(pos: GridPos): boolean {
    const tile = this.getTerrainAt(pos);
    if (!tile) return true; // no terrain = passable
    const cfg = this.registry[tile.type];
    return cfg ? cfg.passable : true;
  }

  grantsStealth(pos: GridPos): boolean {
    const tile = this.getTerrainAt(pos);
    if (!tile) return false;
    const cfg = this.registry[tile.type];
    return cfg ? cfg.grantsStealth : false;
  }

  blocksLOS(pos: GridPos): boolean {
    const tile = this.getTerrainAt(pos);
    if (!tile) return false;
    const cfg = this.registry[tile.type];
    return cfg ? cfg.blocksLineOfSight : false;
  }

  getAllTerrain(): TerrainTile[] {
    return Array.from(this.terrainMap.values());
  }

  // ------------------------------------------------------------------
  // Rendering
  // ------------------------------------------------------------------

  private renderTerrainSprite(tile: TerrainTile): void {
    const cfg = this.registry[tile.type];
    if (!cfg) return;

    const x = this.boardOriginX + tile.pos.col * this.cellSize + this.cellSize / 2;
    const y = this.boardOriginY + tile.pos.row * this.cellH + this.cellH / 2;

    // Pick a random variant sprite
    const variantKey =
      cfg.variants.length > 0
        ? cfg.variants[Phaser.Math.Between(0, cfg.variants.length - 1)]
        : cfg.spriteKey;

    const sprite = this.scene.add.sprite(x, y, variantKey);
    sprite.setDisplaySize(this.cellSize, this.cellH);
    sprite.setDepth(1); // above board, below pieces

    const key = this.posKey(tile.pos);
    this.terrainSprites.set(key, sprite);
  }

  // ------------------------------------------------------------------
  // Helpers
  // ------------------------------------------------------------------

  private posKey(pos: GridPos): string {
    return `${pos.col},${pos.row}`;
  }

  reset(): void {
    this.clearTerrain();
  }

  destroy(): void {
    this.clearTerrain();
  }
}
